import { useState, useMemo } from 'react';
import { RxCross2 } from 'react-icons/rx';

const MultiAgentAssignmentModal = ({
  isOpen,
  agents,
  selectedAgents,
  currentAgents = [],
  reason = '',
  onReasonChange,
  requireReason = false,
  reasonLabel = 'Reason',
  isSubmitting,
  onClose,
  onToggleAgent,
  onSubmit,
  title = 'Select Agents',
}) => {
  const isReasonMissing = requireReason && !reason.trim();

  const [search, setSearch] = useState('');

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchedAgents = q
      ? agents.filter((agent) => {
          const name = `${agent.firstName || ''} ${agent.lastName || ''}`.toLowerCase();
          return name.includes(q);
        })
      : agents;

      const selectedAgentIds = new Set(currentAgents);

    return [
      ...matchedAgents.filter((agent) => selectedAgentIds.has(agent._id)),
      ...matchedAgents.filter((agent) => !selectedAgentIds.has(agent._id)),
    ];
    }, [agents, currentAgents, search]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg w-full max-w-md p-6 mx-4 max-h-[500px] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-5 right-2 text-gray-700 hover:text-gray-900"
        >
          <RxCross2 size={20} />
        </button>

        <h2 className="text-xl font-semibold mb-6">{title}</h2>

        <div className="space-y-3">
          <div className="mb-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agents..."
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          {filteredAgents.map((agent) => (
            <label
              key={agent._id}
              className="flex items-center gap-3 p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <input
                type="checkbox"
                checked={selectedAgents.includes(agent._id)}
                onChange={() => onToggleAgent(agent._id)}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-800">
                {agent.firstName} {agent.lastName}
              </span>
            </label>
          ))}
        </div>

        {onReasonChange && (
          <div className="mt-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {reasonLabel}
            </label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              className="w-full h-24 p-3 border border-gray-300 rounded-md resize-none"
              placeholder="Enter reason..."
            />
            {isReasonMissing && (
              <p className="text-xs text-red-600 mt-1">Reason is required.</p>
            )}
          </div>
        )}

        <div className="mt-8">
          <button
            onClick={onSubmit}
            disabled={isSubmitting || isReasonMissing}
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              isSubmitting || isReasonMissing
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-[#005B96] text-white hover:bg-[#004878]'
            }`}
          >
            {isSubmitting ? 'Updating...' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiAgentAssignmentModal;